import { answersMap } from 'shared/utilities/format';

export const ATTEMPT_QUESTIONNAIRE_ERROR = {
  MALFORMED: 'malformed',
  EMPTY: 'empty',
  DUPLICATE: 'duplicate',
  MISSING_ID: 'missing_id',
};

/**
 * @typedef {object} AttemptAnswerChoice
 * @property {number|string} id
 * @property {string} [text]
 * @property {string} [text_ar]
 * @property {string} [text_en]
 * @property {string} [title]
 */

/**
 * @typedef {object} AttemptQuestion
 * @property {number|string} [id]
 * @property {string} [import_key]
 * @property {string} [text]
 * @property {string} [text_ar]
 * @property {string} [text_en]
 * @property {string} [explanation]
 * @property {AttemptAnswerChoice[]} [answers]
 */

/**
 * @typedef {object} QuestionGroup
 * @property {number} [group_number]
 * @property {AttemptQuestion[]} questions
 */

/**
 * @typedef {object} AttemptQuestionnaire
 * @property {object} [attempt]
 * @property {object|null} [assessment]
 * @property {number|string} [attempt_id]
 * @property {number} total_questions
 * @property {number|null} group_size
 * @property {number} total_groups
 * @property {QuestionGroup[]} question_groups
 */

/**
 * Stable question identity from Laravel. Never use the array index.
 * @param {AttemptQuestion|null|undefined} question
 * @returns {string|number|null}
 */
export function questionIdentity(question) {
  if (!question || typeof question !== 'object') {
    return null;
  }
  if (question.id != null && question.id !== '') {
    return question.id;
  }
  if (question.import_key != null && question.import_key !== '') {
    return question.import_key;
  }
  return null;
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function optionalPositiveInt(value) {
  if (value == null || value === '') {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

/**
 * Validates the Laravel attempt questionnaire envelope.
 * Keeps backend group and question order. Does not infer groups from sections.
 *
 * @param {unknown} payload
 * @returns {{ ok: true, data: AttemptQuestionnaire } | { ok: false, error: string }}
 */
export function adaptAttemptQuestionnaire(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { ok: false, error: ATTEMPT_QUESTIONNAIRE_ERROR.MALFORMED };
  }

  const groups = payload.question_groups;
  if (!Array.isArray(groups)) {
    return { ok: false, error: ATTEMPT_QUESTIONNAIRE_ERROR.MALFORMED };
  }
  if (groups.length === 0) {
    return { ok: false, error: ATTEMPT_QUESTIONNAIRE_ERROR.EMPTY };
  }

  const seen = new Set();
  const question_groups = [];

  for (const group of groups) {
    if (!group || typeof group !== 'object' || Array.isArray(group) || !Array.isArray(group.questions)) {
      return { ok: false, error: ATTEMPT_QUESTIONNAIRE_ERROR.MALFORMED };
    }
    if (group.questions.length === 0) {
      return { ok: false, error: ATTEMPT_QUESTIONNAIRE_ERROR.MALFORMED };
    }

    const questions = [];
    for (const question of group.questions) {
      const identity = questionIdentity(question);
      if (identity == null) {
        return { ok: false, error: ATTEMPT_QUESTIONNAIRE_ERROR.MISSING_ID };
      }
      const key = String(identity);
      if (seen.has(key)) {
        return { ok: false, error: ATTEMPT_QUESTIONNAIRE_ERROR.DUPLICATE };
      }
      seen.add(key);
      questions.push(question);
    }

    question_groups.push({
      group_number: group.group_number,
      questions,
    });
  }

  const attempt = payload.attempt && typeof payload.attempt === 'object' ? payload.attempt : null;
  const totalFromPayload = optionalPositiveInt(payload.total_questions);

  return {
    ok: true,
    data: {
      attempt,
      assessment: payload.assessment && typeof payload.assessment === 'object' ? payload.assessment : null,
      attempt_id: payload.attempt_id ?? attempt?.id ?? null,
      total_questions: totalFromPayload || seen.size,
      group_size: optionalPositiveInt(payload.group_size),
      total_groups: optionalPositiveInt(payload.total_groups) || question_groups.length,
      question_groups,
    },
  };
}

/**
 * Flatten backend groups in the exact provided order. No sort or regroup.
 * @param {QuestionGroup[]} groups
 * @returns {AttemptQuestion[]}
 */
export function questionsFromGroups(groups) {
  if (!Array.isArray(groups)) {
    return [];
  }
  return groups.flatMap((group) => (Array.isArray(group?.questions) ? group.questions : []));
}

/**
 * First group that still has an unanswered question. If every question is
 * answered, open the last group so the existing submit action remains available.
 * @param {QuestionGroup[]} groups
 * @param {Array<{ question_id?: number, answer_id?: number }>} selectedAnswers
 * @returns {number}
 */
export function firstIncompleteGroupIndex(groups, selectedAnswers = []) {
  if (!Array.isArray(groups) || groups.length === 0) {
    return 0;
  }
  const answered = new Set(
    (selectedAnswers || [])
      .filter((row) => row?.question_id != null && row?.answer_id != null)
      .map((row) => String(row.question_id)),
  );
  const index = groups.findIndex((group) => (
    (group.questions || []).some((question) => !answered.has(String(questionIdentity(question))))
  ));
  return index < 0 ? groups.length - 1 : index;
}

/**
 * @param {AttemptQuestion[]} questions
 * @param {Record<string|number, number|string>} selected
 * @returns {AttemptQuestion|undefined}
 */
export function firstUnansweredQuestion(questions, selected) {
  return (questions || []).find((question) => {
    const identity = questionIdentity(question);
    return identity != null && selected[identity] == null && selected[Number(identity)] == null;
  });
}

/**
 * @param {AttemptQuestion[]} questions
 * @param {Record<string|number, number|string>} selected
 * @returns {Array<{ question_id: number, answer_id: number }>}
 */
export function selectedAnswersPayload(questions, selected) {
  return (questions || []).reduce((rows, question) => {
    const identity = questionIdentity(question);
    if (identity == null) {
      return rows;
    }
    const answerId = selected[identity] ?? selected[Number(identity)];
    if (answerId == null) {
      return rows;
    }
    rows.push({
      question_id: Number(identity),
      answer_id: Number(answerId),
    });
    return rows;
  }, []);
}

/**
 * Inclusive 1-based range of questions in the current backend group.
 * @param {QuestionGroup[]} groups
 * @param {number} groupIndex
 * @returns {{ start: number, end: number, total: number }}
 */
export function questionRangeForGroup(groups, groupIndex, totalQuestions) {
  const before = (groups || [])
    .slice(0, Math.max(0, groupIndex))
    .reduce((sum, group) => sum + (group.questions?.length || 0), 0);
  const currentCount = groups?.[groupIndex]?.questions?.length || 0;
  return {
    start: currentCount > 0 ? before + 1 : 0,
    end: before + currentCount,
    total: Number(totalQuestions) || questionsFromGroups(groups).length,
  };
}

export { answersMap };
